import {
  differenceInCalendarDays,
  endOfMonth,
  format as dfFormat,
  isAfter,
  isWeekend,
  Locale,
  parseISO,
  subDays,
} from 'date-fns';
import { enUS as enLocale } from 'date-fns/locale/en-US';
import { fr as frLocale } from 'date-fns/locale/fr';

type InputDate = Date | string | number;

/** --- i18n → date-fns locale resolver (plug-in) --- */
let _localeResolver: (() => string | undefined) | null = null;
/** Appelez ceci une fois après l'init i18next: setDateLocaleResolver(() => i18next.language) */
export const setDateLocaleResolver = (fn: () => string | undefined) => {
  _localeResolver = fn;
};

function mapLangToDateFnsLocale(lang?: string): Locale {
  // normalise: "fr-FR" → "fr", "en-GB" → "en"
  const base = (lang || '').toLowerCase().split('-')[0];
  switch (base) {
    case 'en':
      return enLocale;
    case 'fr':
      return frLocale;
    default:
      return frLocale; // défaut: FR
  }
}

const capitalizeFirstLetter = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Safely converts any input (Date | ISO string | timestamp) to a Date object */
function toDate(d: InputDate): Date {
  if (d instanceof Date) return d;
  if (typeof d === 'number') return new Date(d);
  if (typeof d === 'string') {
    // Prefer ISO format (yyyy-MM-dd, yyyy-MM-ddTHH:mm:ssZ, etc.)
    const iso = parseISO(d);
    if (!Number.isNaN(iso.getTime())) return iso;
    // Fallback: let the JS engine parse it (locale-dependent)
    const parsed = new Date(d);
    return Number.isNaN(parsed.getTime()) ? new Date(NaN) : parsed;
  }
  return new Date(d);
}

/**
 * Universal date formatter with built-in presets.
 *
 * @param {InputDate} date - Date instance, ISO string, or timestamp
 * @param {'month'|'long'|'short'|'isoDate'|'isoMonth'|'isoWeek'|'isoWeekYear'|string} pattern - Format preset or custom string
 * @param {boolean} [capitalize=false] - Capitalize the first letter (for French readability)
 * @returns {string}
 */
export function formatDate(
  date: InputDate,
  pattern:
    | 'monthShort' // "Jan 2025" (en) / "janv. 2025" (fr)
    | 'month' // "March 2025" / "mars 2025"
    | 'monthName' // "March" / "mars"
    | 'long' // "Monday 3 March 2025" / "lundi 3 mars 2025"
    | 'short' // "03/03/2025"
    | 'human' // "3 mars 2025" (fr) / "3 Mar 2025" (en)
    | 'humanDatetime' // "3 mars 2025 à 14h32" (fr) / "3 Mar 2025 at 14:32" (en)
    | 'datetime' // "03/03/2025 · 14:32"
    | 'datetimeSeconds' // "03/03/2025 · 14:32:07"
    | 'isoDate' // "2025-03-03"
    | 'isoMonth' // "2025-03"
    | 'isoWeek' // "10"
    | 'isoWeekYear' // "2025-W10"
    | 'weekdayMini' // "Lun. 01/08/2025" (fr) / "Mon 08/01/2025" (en)
    | 'weekdayShort' // "Lundi 01/08/2025" (fr) / "Monday 08/01/2025" (en)
    | string = 'long',
  capitalize: boolean = false,
): string {
  const map: Record<string, string> = {
    monthShort: 'MMM yyyy',
    month: 'MMMM yyyy',
    monthName: 'LLLL',
    long: 'EEEE d MMMM yyyy',
    short: 'dd/MM/yyyy',
    /**
     * Formats lisibles, pour une date qu'on **lit** plutôt qu'on ne compare.
     *
     * `dd/MM/yyyy` se compare d'une ligne à l'autre par sa forme fixe : il reste
     * le bon choix pour une plage (`01/09/2025 → 31/08/2027`), où les deux
     * bornes doivent s'aligner. Une date isolée, elle, se lit — et « 14 févr.
     * 2026 » se lit sans décoder l'ordre jour/mois.
     *
     * Le séparateur d'heure suit l'usage de la langue : « à 14h32 » en français,
     * « at 14:32 » en anglais. Un `14h32` anglais ne se lit pas.
     */
    human: 'd MMM yyyy',
    humanDatetime: '', // résolu plus bas : le séparateur dépend de la langue
    datetime: '', // résolu plus bas : le séparateur dépend de la langue
    /* Horodatage technique — journal d'audit, traces. Les deux-points restent la forme lisible
       quand les secondes comptent : « 10h55m32 » ne s'écrit pas. */
    datetimeSeconds: 'dd/MM/yyyy · HH:mm:ss',
    isoDate: 'yyyy-MM-dd',
    isoMonth: 'yyyy-MM',
    isoWeek: 'I',
    isoWeekYear: "RRRR-'W'I",
    weekdayMini: 'EEE dd/MM/yyyy',
    weekdayShort: 'EEEE dd/MM/yyyy',
  };

  const lang = _localeResolver?.();
  const locale = mapLangToDateFnsLocale(lang);

  // Le séparateur d'heure suit l'usage de la langue : « à 14h32 » ne se lit pas
  // en anglais, « at 14:32 » ne se lit pas en français.
  if (pattern === 'humanDatetime') {
    map.humanDatetime = locale === frLocale ? "d MMM yyyy 'à' HH'h'mm" : "d MMM yyyy 'at' HH:mm";
  }

  /*
    **La même règle, appliquée au format court** (#2010).

    Elle était écrite juste au-dessus et ne valait que pour `humanDatetime` : `datetime` affichait
    « 25/08/2026 · 10:55 » en français, là où l'usage écrit « 10h55 ». Une convention qui ne tient
    qu'à un endroit finit par ne plus être une convention.
  */
  if (pattern === 'datetime') {
    map.datetime = locale === frLocale ? "dd/MM/yyyy · HH'h'mm" : 'dd/MM/yyyy · HH:mm';
  }

  const fmt = map[pattern] ?? pattern;

  const out = dfFormat(toDate(date), fmt, { locale });
  return capitalize ? capitalizeFirstLetter(out) : out;
}

/**
 * Returns the last business day of a given month.
 * If the month's last day falls on a weekend, it moves backward
 * until it reaches the nearest weekday.
 *
 * @param {Date} date - Any date within the target month
 * @returns {Date} - The last business day of that month
 */
export const getLastBusinessDayOfMonth = (date: Date): Date => {
  let lastDay = endOfMonth(date);
  while (isWeekend(lastDay)) {
    lastDay = subDays(lastDay, 1);
  }
  return lastDay;
};

function lastBusinessDayOfMonth(y: number, m: number): Date {
  const end = endOfMonth(new Date(y, m - 1, 1));
  const d = new Date(end);
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() - 1);
  }
  return d;
}

const NOW = new Date();
const CUR_YEAR = NOW.getFullYear();
const CUR_MONTH = NOW.getMonth() + 1; // 1-12

export function isBeforeLastBusinessDayOfCurrentMonth(y: number, m: number): boolean {
  if (y !== CUR_YEAR || m !== CUR_MONTH) return false;
  const lastBD = lastBusinessDayOfMonth(y, m);
  // On considère “avant l’échéance” tant que la date du jour est strictement avant le dernier jour ouvré
  return NOW < new Date(lastBD.getFullYear(), lastBD.getMonth(), lastBD.getDate(), 23, 59, 59);
}

/**
 * Formats a mission period into a readable French string.
 * Examples:
 *  - "Janvier 2024 - Présent" if the mission is ongoing or no end date is given
 *  - "Janvier 2024 - Juin 2024" if it has an end date in the past
 *
 * Handles invalid or missing dates gracefully.
 *
 * @param {string} startDate - Mission start date (ISO string)
 * @param {string | null} [endDate] - Mission end date (optional)
 * @returns {{ period: string; isOngoing: boolean }}
 *  - `period`: formatted mission period string
 *  - `isOngoing`: true if the mission is still ongoing
 */
export const formatMissionPeriod = (
  startDate: string,
  endDate?: string | null,
): { period: string; isOngoing: boolean } => {
  const start = toDate(startDate);
  const end = endDate ? toDate(endDate) : null;
  const now = new Date();

  const startOk = !Number.isNaN(start.getTime());
  const endOk = end && !Number.isNaN(end.getTime());

  const formattedStart = startOk ? formatDate(start, 'month', true) : 'Invalid start date';

  // Consider ongoing if there is no end date or the end is in the future
  const isOngoing = !endOk || isAfter(end!, now);

  const formattedEnd = isOngoing ? 'Présent' : formatDate(end!, 'month', true);

  // Safety check: if the end date is before the start date,
  // keep the chronological order without forcing "Présent"
  if (startOk && endOk && end! < start) {
    return {
      period: `${formattedStart} - ${formatDate(end!, 'month', true)}`,
      isOngoing: false,
    };
  }

  return {
    period: `${formattedStart} - ${formattedEnd}`,
    isOngoing,
  };
};

/**
 * Formats total minutes into a human-readable string (e.g., "2h05", "7h", "0h").
 *
 * @param {number | undefined} total - Total minutes
 * @returns {string} - Formatted time string
 */
export const formatMinutes = (total: number | undefined) => {
  if (!total || total <= 0) return '0h';
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
};

export const weekOfISO = (isoDate: string) => {
  const d = new Date(isoDate + 'T00:00:00');
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target.valueOf() - firstThursday.valueOf();
  return 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
};

export function getCurrentPeriod() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * Libellé de temps relatif « à la mobile » (FR), couplé à la date exacte dans l'UI :
 * « à l'instant », « il y a 32 min », « il y a 3 h », « hier », « il y a 4 jours »,
 * « il y a 1 semaine », « il y a 2 semaines », « il y a 3 mois », « il y a 1 an ».
 * `now` est injectable pour les tests. Renvoie `null` si la date est invalide.
 */
export function formatRelativeFr(iso?: string | null, now: Date = new Date()): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const dayStart = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.round((dayStart(now) - dayStart(d)) / 86_400_000);

  // Futur (léger décalage d'horloge) → on reste sur « à l'instant ».
  if (diffMs < 0 && diffDays <= 0) return "à l'instant";

  if (diffDays === 0) {
    const min = Math.floor(diffMs / 60_000);
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.floor(min / 60);
    return `il y a ${h} h`;
  }
  if (diffDays === 1) return 'hier';
  if (diffDays < 7) return `il y a ${diffDays} jours`;
  if (diffDays < 14) return 'il y a 1 semaine';
  if (diffDays < 30) return `il y a ${Math.floor(diffDays / 7)} semaines`;
  if (diffDays < 365) {
    const months = Math.max(1, Math.floor(diffDays / 30));
    return `il y a ${months} mois`;
  }
  const years = Math.floor(diffDays / 365);
  return `il y a ${years} an${years > 1 ? 's' : ''}`;
}

/**
 * Nombre de **jours de calendrier** écoulés depuis une date, dans le fuseau du
 * lecteur.
 *
 * À ne pas confondre avec une durée divisée par vingt-quatre heures : une
 * connexion du 6 août à 23 h 07 lue le 7 août à 22 h ne fait pas encore
 * vingt-quatre heures, et se déclarait donc « aujourd'hui » alors qu'elle date
 * de la veille. C'est la frontière de minuit qui compte, pas l'écoulement.
 *
 * `differenceInCalendarDays` raisonne en dates locales : l'ISO renvoyé par
 * l'API est en UTC, sa conversion en heure locale fait le reste.
 *
 * Rend `null` sur une date absente ou illisible — `0` s'y lirait « aujourd'hui ».
 */
export function calendarDaysSince(date: InputDate, now: Date = new Date()): number | null {
  if (!date) return null;
  const d = toDate(date);
  if (Number.isNaN(d.getTime())) return null;
  return differenceInCalendarDays(now, d);
}

/** Symétrique, vers le futur : jours de calendrier restants avant une date. */
export function calendarDaysUntil(date: InputDate, now: Date = new Date()): number | null {
  if (!date) return null;
  const d = toDate(date);
  if (Number.isNaN(d.getTime())) return null;
  return differenceInCalendarDays(d, now);
}

/**
 * Ancienneté en clair : « aujourd'hui », « hier », « il y a 12 j ».
 *
 * « hier » est nommé plutôt que compté : « il y a 1 j » se lit moins bien, et
 * c'est précisément la valeur qu'on confondait avec « aujourd'hui ».
 */
export function agoLabel(days: number | null): string | null {
  if (days === null) return null;
  if (days <= 0) return 'aujourd’hui';
  if (days === 1) return 'hier';
  return `il y a ${days} j`;
}

/**
 * **Un nombre écrit dans la langue de l'écran** (#2387).
 *
 * ⚠️ `toLocaleString('fr-FR')` était figé dans une dizaine d'endroits de la console : `1 234`
 * séparé par une espace insécable, au milieu d'un écran passé en anglais. C'est le même symptôme
 * que le texte à moitié traduit, et il se voit autant — un chiffre n'a pas de langue, sa
 * ponctuation en a une.
 *
 * On réemploie le résolveur des dates plutôt que d'en poser un second : deux sources de langue
 * finiraient par diverger, et c'est l'écran qui montrerait la différence.
 */
export function formatNumber(value: number | null | undefined, absent = '—'): string {
  if (value == null || Number.isNaN(value)) return absent;
  return value.toLocaleString(_localeResolver?.() || 'fr-FR');
}
